import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Button,
  Flex,
  Input,
  Textarea,
  Divider,
  Text,
  HStack,
  Tooltip,
  Box,
  Switch,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'

import { ChipsInput, FormItem, IconButtonWithPermission } from 'components'
import { focusElementById } from 'utils/events'
import { Option } from 'types'
import { CustomFormQuestion, CustomFormScoreClassification } from 'components/tools/CustomForm/types'
import { useCustomFormBuilder } from 'components/tools/CustomForm/components/Builder/contexts/CustomFormBuilderManagerContext/Provider'
import { Pencil } from 'components/icons'
import KindButton from './components/KindButton'
import OptionsManagement from './components/OptionsManagement'
import Validations from './components/Validations'
import Demonstration from './components/Demonstration'
import ScoreOptions from './components/ScoreOptions'
import DropzoneOnCustomForm from './components/DropzoneOnCustomForm'
import EntityKindOptions from './components/EntityKindOptions'
import EntityKindDocuments from './components/EntityKindDocuments'
import CustomFormBuilderConfigsLayout from 'components/tools/CustomForm/commonComponents/CustomFormBuilderConfigsLayout'
import DefaultValueField from './components/DefaultValueField'

type QuestionProps = {
  question: CustomFormQuestion
}

export default function CustomFormBuilderEditQuestionButton({ question }: QuestionProps) {
  const [loadingButton, setLoadingButton] = useState(false)
  const [unsavedChanges, setUnsavedChanges] = useState(false)

  const { isOpen, onOpen, onClose } = useDisclosure()

  const { isFormActionsDisabled, permissions, updateQuestion } = useCustomFormBuilder()

  function verifyScoreDefaultValues(questionDefaultValues: CustomFormQuestion) {
    const defaultValues = questionDefaultValues

    if (questionDefaultValues?.score_settings) {
      if (questionDefaultValues?.score_settings?.options && questionDefaultValues?.score_settings?.options.length > 0) {
        questionDefaultValues.score_settings.options.forEach((element: Option, index: number) => {
          defaultValues[`score_${index}`] = element.value
        })
      }
    }

    return defaultValues
  }

  const questionDefaultValues = {
    title: question.title,
    description: question.description,
    kind: question.kind,
    entity: question.entity,
    options: question.options || [],
    is_required: question.is_required,
    placeholder: '',
    has_score: question.has_score,
    score_settings: question.score_settings || { options: [] },
    settings: question.settings,
    default_value: question.default_value,
    validation: question.validation,
    tags: question.tags,
  }

  const formMethods = useForm<CustomFormQuestion>({
    defaultValues: verifyScoreDefaultValues(questionDefaultValues),
  })

  const { isDirty } = formMethods.formState

  const title = formMethods.watch('title')
  const entity = formMethods.watch('entity')
  const settings = formMethods.watch('settings')
  const validation = formMethods.watch('validation')
  const description = formMethods.watch('description')
  const placeholder = formMethods.watch('placeholder')
  const kind = formMethods.watch('kind')
  const options = formMethods.watch('options')
  const is_required = formMethods.watch('is_required')
  const tags = formMethods.watch('tags')

  const watchQuestions = {
    entity,
    validation,
    settings,
    title,
    description,
    placeholder,
    kind,
    options,
    is_required,
    default_value: question.default_value,
  }

  const hasScoreOptions = useMemo(() => {
    const kinds = ['SINGLE_SELECTION_BOX', 'MULTIPLE_SELECTION_BOX', 'SINGLE_SELECTION_LIST']
    return kinds.includes(kind || '')
  }, [kind])

  const hasDocumentUploads = useMemo(() => {
    const kinds = ['DOCUMENT']
    const entities = ['CPF']
    return entities.includes(entity || '') && kinds.includes(kind || '')
  }, [kind, entity])

  function setScoreSettingsOnForm() {
    const scoreInputsValues: CustomFormScoreClassification = {}

    if (question.score_settings && question.score_settings.options) {
      question.score_settings.options.map((option: { key: string; value: string }, index: number) => {
        scoreInputsValues[`score_${index}`] = option.value
      })
    }
    return scoreInputsValues
  }

  async function onSubmit(values: CustomFormQuestion) {
    setLoadingButton(true)

    if ((values.default_value && !Array.isArray(values.default_value)) || values.default_value === '') {
      values.default_value = [values.default_value]
    }

    const response = await updateQuestion({ data: values, questionId: question.id })

    if (response) onClose()
    setLoadingButton(false)
  }

  useEffect(() => {
    const tagsValue = tags ?? []
    const questionTags = question.tags ?? []

    const isDifferent =
      tagsValue.length !== questionTags.length || tagsValue.some((val: string, i: number) => val !== questionTags[i])

    setUnsavedChanges(isDirty || isDifferent)
  }, [isDirty, tags, question.tags])

  useEffect(() => {
    const scoreValues = setScoreSettingsOnForm()
    formMethods.reset({ ...questionDefaultValues, ...scoreValues })
  }, [question])

  useEffect(() => {
    focusElementById('title')
  }, [])

  if (isFormActionsDisabled) {
    return (
      <Tooltip label="Este formulário é protegido, não pode ser alterado / deletado">
        <Box>
          <IconButtonWithPermission
            isDisabled={isFormActionsDisabled}
            icon={<Pencil />}
            aria-label="icon"
            variant="ghost"
            size="sm"
          />
        </Box>
      </Tooltip>
    )
  }

  return (
    <>
      <IconButtonWithPermission
        customDisableCondition={permissions?.updateFormQuestion}
        icon={<Pencil />}
        aria-label="icon"
        variant="ghost"
        size="sm"
        autoFocus={false}
        onClick={onOpen}
      />

      <Drawer isOpen={isOpen} size="sm" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{question?.title}</DrawerHeader>

          <DrawerBody p={0}>
            <Box as="form" id="update-custom-form-question" onSubmit={formMethods.handleSubmit(onSubmit)}>
              <Flex flexDir="column">
                <Flex bg="primary.100" _dark={{ bg: 'primary.800' }} flexDir="column" p={4}>
                  <HStack>
                    <Divider />
                    <Text textStyle="md" fontWeight="medium" whiteSpace="nowrap">
                      Prévia
                    </Text>
                    <Divider />
                  </HStack>
                  <Demonstration question={watchQuestions} />
                </Flex>
                <Flex flexDir="column" p={4} gap={4} w="full">
                  <HStack>
                    <Divider />
                    <Text textStyle="md" fontWeight="medium" whiteSpace="nowrap">
                      Informações
                    </Text>
                    <Divider />
                  </HStack>

                  <FormItem isRequired label="Questão">
                    <Input autoFocus {...formMethods.register('title')} />
                  </FormItem>

                  <FormItem label="Texto de ajuda">
                    <Textarea {...formMethods.register('description')} />
                  </FormItem>

                  <KindButton question={watchQuestions} formMethods={formMethods} />

                  {kind === 'ENTITY' && <EntityKindOptions formMethods={formMethods} />}

                  {kind === 'DOCUMENT' && <EntityKindDocuments formMethods={formMethods} />}

                  {kind === 'SINGLE_SELECTION_BOX' && (
                    <OptionsManagement options={options as string[]} type={'RADIO'} formMethods={formMethods} />
                  )}
                  {kind === 'MULTIPLE_SELECTION_BOX' && (
                    <OptionsManagement options={options as string[]} type={'CHECKBOX'} formMethods={formMethods} />
                  )}
                  {kind === 'SINGLE_SELECTION_LIST' && (
                    <OptionsManagement options={options as string[]} type={'SELECT'} formMethods={formMethods} />
                  )}

                  {kind === 'SINGLE_FILE_UPLOAD' && <DropzoneOnCustomForm />}

                  <CustomFormBuilderConfigsLayout>
                    <>
                      {hasDocumentUploads && (
                        <FormItem label="Permitir atualização de documentos">
                          <Switch {...formMethods.register('settings.document_has_uploads')} />
                        </FormItem>
                      )}

                      <DefaultValueField formMethods={formMethods} question={watchQuestions} />

                      <FormItem label="Tags">
                        <ChipsInput
                          values={(formMethods.watch('tags') as string[]) || []}
                          onChange={(values: string[]) => formMethods.setValue('tags', values)}
                        />
                      </FormItem>

                      <FormItem label="Permitir validação de resposta">
                        <Switch {...formMethods.register('settings.has_data_validation')} />
                      </FormItem>
                    </>
                  </CustomFormBuilderConfigsLayout>

                  {hasScoreOptions && (
                    <CustomFormBuilderConfigsLayout>
                      <ScoreOptions kind={kind} formMethods={formMethods} />
                    </CustomFormBuilderConfigsLayout>
                  )}

                  <CustomFormBuilderConfigsLayout text="Validações">
                    <Validations formMethods={formMethods} questionKind={kind} />
                  </CustomFormBuilderConfigsLayout>
                </Flex>
              </Flex>
            </Box>
          </DrawerBody>

          <DrawerFooter flexDirection={'column'}>
            {unsavedChanges && (
              <Alert status="warning" rounded={'md'}>
                <AlertIcon />
                Você tem alterações não salvas
              </Alert>
            )}

            <Flex gap={4} justifyContent={'end'} w="full">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button isLoading={loadingButton} form="update-custom-form-question" type="submit">
                Atualizar questão
              </Button>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  )
}
